import type { ComponentPropsWithoutRef, MouseEvent } from 'react';
import { forwardRef, useContext, useMemo } from 'react';
import { unstable_useId as useId } from 'reakit';
import { pxToRem } from '@mos-cat/ds-theme';
import { useRegisterRow } from '../internal/useRegisterRow';
import { Box } from '../../Box/Box';
import { Row } from '../internal/Row';
import { Link } from '../../Link/Link';
import { Text } from '../../Text/Text';
import { Hint } from '../../Hint/Hint';
import { Stack } from '../../Stack/Stack';
import type { OptionParams } from '../internal/types';
import { searchContext } from '../internal/context';

type LinkOptionProps = Omit<ComponentPropsWithoutRef<'div'>, 'role'> &
  Pick<ComponentPropsWithoutRef<'a'>, 'href'> & {
    hint?: string;
    value: string;
    href: string;
  };

export const LinkOption = forwardRef<HTMLDivElement, LinkOptionProps>(
  function LinkOption({ children, hint, value, href, ...props }, ref) {
    let { id } = useId(props) as { id: string };
    const cellIds = useMemo<OptionParams['cellIds']>(
      function getCellIds() {
        return [`${id}-content`];
      },
      [id]
    );
    useRegisterRow({
      type: 'link',
      href,
      id,
      value,
      cellIds,
    });

    const { send } = useContext(searchContext);
    return (
      <Row
        ref={ref}
        {...props}
        id={id}
        pl={['sp-12', pxToRem(46)]}
        onMouseUp={(event: MouseEvent<HTMLDivElement>) => {
          props.onMouseUp?.(event);
          if (event.defaultPrevented) return;
          event.preventDefault();
          event.stopPropagation();
          send({ type: 'select', id });
        }}
      >
        <Box role="gridcell" styles={{ label: 'LinkCell' }}>
          <Stack
            space="0"
            onClick={(event: MouseEvent<HTMLAnchorElement>) =>
              event.preventDefault()
            }
            as="a"
            href={href}
            id={cellIds[0]}
          >
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- ESLint ошибочно считает что это ссылка */}
            <Link
              as={Text}
              styles={{
                textDecoration: 'none',
                '*:visited > &': { color: 'linkVisitedColor' },
              }}
            >
              {children}
            </Link>
            {hint && <Hint>{hint}</Hint>}
          </Stack>
        </Box>
      </Row>
    );
  }
);

LinkOption.displayName = 'LinkOption';
